/**
 * @fileoverview Forcing a flag's answer while you test — `flags.override()` and
 * `?fg_flags=key:variant`.
 * @module lib/features/core/flagOverrides
 *
 * A developer checking the "urgent" arm of an experiment should not have to
 * clear cookies until the hash lands them there. An override answers the read
 * directly, lasts for the tab, and is never counted: no usage digest, no
 * exposure. A forced visitor is not a participant, and counting them is how a
 * test you clicked through ten times shows up in the report.
 *
 * The URL form is honoured only on a local host, or when the website opts in
 * with `flags: { allowUrlOverrides: true }`. On a public page, a query string
 * anyone can type must not be able to switch on a flag that gates something.
 */

import { isTrackableHost } from './trackableHost';
import type { CompiledFlag, FlagDecision } from './flagEval';

/** A forced answer: `true` / `false` for a gate, or the name of an arm. */
export type FlagOverride = boolean | string;

export type FlagOverrides = Record<string, FlagOverride>;

export const OVERRIDE_PARAM = 'fg_flags';

const STORAGE_KEY = (webId: string) => `fg_flag_overrides_${webId}`;

const ON_WORDS = new Set(['on', 'true', '1']);
const OFF_WORDS = new Set(['off', 'false', '0']);

/** `on`/`off` read as a gate's answer; anything else names an arm. */
function parseValue(raw: string): FlagOverride | null {
  const value = raw.trim();
  if (!value) return null;
  if (ON_WORDS.has(value.toLowerCase())) return true;
  if (OFF_WORDS.has(value.toLowerCase())) return false;
  return value;
}

/**
 * `checkout-copy:urgent,new-nav:on` → `{ 'checkout-copy': 'urgent', 'new-nav': true }`.
 * A key with no value forces it on. Malformed pairs are skipped, never thrown.
 */
export function parseOverrideParam(param: string): FlagOverrides {
  const out: FlagOverrides = {};
  for (const pair of param.split(',')) {
    const [rawKey, ...rest] = pair.split(':');
    const key = rawKey?.trim();
    if (!key) continue;

    const value = rest.length ? parseValue(rest.join(':')) : true;
    if (value !== null) out[key] = value;
  }
  return out;
}

function storage(): Storage | null {
  try {
    return typeof sessionStorage !== 'undefined' ? sessionStorage : null;
  } catch {
    return null;
  }
}

export function readStoredOverrides(webId: string): FlagOverrides {
  try {
    const raw = storage()?.getItem(STORAGE_KEY(webId));
    const parsed = raw ? (JSON.parse(raw) as unknown) : null;
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};

    const out: FlagOverrides = {};
    for (const [key, value] of Object.entries(parsed as Record<string, unknown>)) {
      if (typeof value === 'boolean' || (typeof value === 'string' && value)) out[key] = value;
    }
    return out;
  } catch {
    return {};
  }
}

export function writeStoredOverrides(webId: string, overrides: FlagOverrides): void {
  try {
    const store = storage();
    if (!store) return;
    if (Object.keys(overrides).length === 0) store.removeItem(STORAGE_KEY(webId));
    else store.setItem(STORAGE_KEY(webId), JSON.stringify(overrides));
  } catch {
    /* private mode or full storage — the override still applies to this page */
  }
}

/**
 * The overrides in force when the engine loads: whatever this tab stored, plus
 * the URL's when the URL is allowed to set them. `?fg_flags=` with nothing
 * after it clears them.
 */
export function loadOverrides(webId: string, options: { allowUrl: boolean }): FlagOverrides {
  const stored = readStoredOverrides(webId);
  if (typeof window === 'undefined' || !window.location) return stored;

  const urlAllowed = options.allowUrl || !isTrackableHost(window.location.hostname);
  if (!urlAllowed) return stored;

  let param: string | null = null;
  try {
    param = new URLSearchParams(window.location.search).get(OVERRIDE_PARAM);
  } catch {
    return stored;
  }
  if (param === null) return stored;

  const next = param.trim() ? { ...stored, ...parseOverrideParam(param) } : {};
  writeStoredOverrides(webId, next);
  return next;
}

/** The decision an override stands for, read against the flag when we have it. */
export function overrideDecision(flag: CompiledFlag | undefined, forced: FlagOverride): FlagDecision {
  if (typeof forced === 'boolean') {
    const payload = forced && flag?.p !== undefined ? flag.p : undefined;
    return { value: forced, variant: null, ...(payload !== undefined ? { payload } : {}), reason: 'override' };
  }

  const payload = flag?.variants?.[forced];
  return {
    value: payload !== false,
    variant: forced,
    ...(payload !== undefined ? { payload } : {}),
    reason: 'override',
  };
}
